import { ActiveDescendantKeyManager } from '@angular/cdk/a11y';
import * as i0 from '@angular/core';
import { inject, ElementRef, Directive, Input, HostBinding, HostListener, EventEmitter, ChangeDetectorRef, DestroyRef, ContentChildren, Output, NgModule } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

class SiAutocompleteOptionDirective {
    static idCounter = 0;
    element = inject(ElementRef);
    // The explicit type declaration is needed here for some mysterious ts issues
    parent = inject(SiAutocompleteListboxDirective);
    id = `__si-autocomplete-option-${SiAutocompleteOptionDirective.idCounter++}`;
    disabled;
    value;
    active;
    click() {
        this.parent.siAutocompleteOptionSubmitted.emit(this.value);
    }
    /** @internal */
    setActiveStyles() {
        this.active = true;
        this.element.nativeElement.scrollIntoView({ block: 'nearest' });
    }
    /** @internal */
    setInactiveStyles() {
        this.active = false;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAutocompleteOptionDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.1.5", type: SiAutocompleteOptionDirective, isStandalone: true, selector: "[siAutocompleteOption]", inputs: { id: "id", disabled: "disabled", value: ["siAutocompleteOption", "value"] }, host: { attributes: { "role": "option" }, listeners: { "click": "click()" }, properties: { "id": "this.id", "attr.aria-disabled": "this.disabled", "class.active": "this.active" } }, exportAs: ["siAutocompleteOption"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAutocompleteOptionDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siAutocompleteOption]',
                    exportAs: 'siAutocompleteOption',
                    standalone: true,
                    host: {
                        role: 'option'
                    }
                }]
        }], propDecorators: { id: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['id']
            }], disabled: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['attr.aria-disabled']
            }], value: [{
                type: Input,
                args: ['siAutocompleteOption']
            }], active: [{
                type: HostBinding,
                args: ['class.active']
            }], click: [{
                type: HostListener,
                args: ['click']
            }] } });

class SiAutocompleteListboxDirective {
    static idCounter = 0;
    options;
    id = `__si-autocomplete-listbox-${SiAutocompleteListboxDirective.idCounter++}`;
    autocomplete;
    siAutocompleteDefaultIndex = 0;
    siAutocompleteOptionSubmitted = new EventEmitter();
    keyManager;
    changeDetectorRef = inject(ChangeDetectorRef);
    destroyRef = inject(DestroyRef);
    ngOnInit() {
        // For some reason, this is needed sometimes. Otherwise, one may get ExpressionChangedAfterItHasBeenCheckedError.
        queueMicrotask(() => {
            this.changeDetectorRef.markForCheck();
            this.autocomplete.listbox = this;
        });
        this.destroyRef.onDestroy(() => (this.autocomplete.listbox = undefined));
    }
    ngAfterContentInit() {
        this.keyManager = new ActiveDescendantKeyManager(this.options)
            .withWrap(true)
            .withVerticalOrientation(true);
        this.options.changes
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.setActiveItem());
        this.setActiveItem();
    }
    setActiveItem() {
        queueMicrotask(() => {
            this.keyManager.setActiveItem(this.siAutocompleteDefaultIndex);
            this.changeDetectorRef.markForCheck();
        });
    }
    /** @internal */
    onKeydown(event) {
        this.keyManager.onKeydown(event);
        if (event.key === 'Enter' && this.keyManager.activeItem) {
            this.siAutocompleteOptionSubmitted.emit(this.keyManager.activeItem.value);
        }
        this.changeDetectorRef.markForCheck();
    }
    get active() {
        return this.keyManager?.activeItem ?? null;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAutocompleteListboxDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.1.5", type: SiAutocompleteListboxDirective, isStandalone: true, selector: "[siAutocompleteListboxFor]", inputs: { id: "id", autocomplete: ["siAutocompleteListboxFor", "autocomplete"], siAutocompleteDefaultIndex: "siAutocompleteDefaultIndex" }, outputs: { siAutocompleteOptionSubmitted: "siAutocompleteOptionSubmitted" }, host: { attributes: { "role": "listbox" }, properties: { "id": "this.id" } }, queries: [{ propertyName: "options", predicate: SiAutocompleteOptionDirective, descendants: true }], exportAs: ["siAutocompleteListbox"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAutocompleteListboxDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siAutocompleteListboxFor]',
                    standalone: true,
                    exportAs: 'siAutocompleteListbox',
                    host: {
                        role: 'listbox'
                    }
                }]
        }], propDecorators: { options: [{
                type: ContentChildren,
                args: [SiAutocompleteOptionDirective, { descendants: true }]
            }], id: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['id']
            }], autocomplete: [{
                type: Input,
                args: ['siAutocompleteListboxFor']
            }], siAutocompleteDefaultIndex: [{
                type: Input
            }], siAutocompleteOptionSubmitted: [{
                type: Output
            }] } });

class SiAutocompleteDirective {
    /** @internal */
    listbox;
    get activeDescendant() {
        return this.listbox?.active?.id ?? '';
    }
    get ariaControls() {
        return this.listbox?.id;
    }
    get expanded() {
        return !!this.listbox;
    }
    keydown(event) {
        this.listbox?.onKeydown(event);
    }
    get active() {
        return this.listbox?.active;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAutocompleteDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.1.5", type: SiAutocompleteDirective, isStandalone: true, selector: "input[siAutocomplete]", host: { attributes: { "role": "combobox", "aria-autocomplete": "list" }, listeners: { "keydown": "keydown($event)" }, properties: { "attr.aria-activedescendant": "this.activeDescendant", "attr.aria-controls": "this.ariaControls", "attr.aria-expanded": "this.expanded" } }, exportAs: ["siAutocomplete"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAutocompleteDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'input[siAutocomplete]',
                    exportAs: 'siAutocomplete',
                    standalone: true,
                    host: {
                        role: 'combobox',
                        'aria-autocomplete': 'list'
                    }
                }]
        }], propDecorators: { activeDescendant: [{
                type: HostBinding,
                args: ['attr.aria-activedescendant']
            }], ariaControls: [{
                type: HostBinding,
                args: ['attr.aria-controls']
            }], expanded: [{
                type: HostBinding,
                args: ['attr.aria-expanded']
            }], keydown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }] } });

class SiAutocompleteModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAutocompleteModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.1.5", ngImport: i0, type: SiAutocompleteModule, imports: [SiAutocompleteDirective, SiAutocompleteListboxDirective, SiAutocompleteOptionDirective], exports: [SiAutocompleteDirective, SiAutocompleteListboxDirective, SiAutocompleteOptionDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAutocompleteModule });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAutocompleteModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [SiAutocompleteDirective, SiAutocompleteListboxDirective, SiAutocompleteOptionDirective],
                    exports: [SiAutocompleteDirective, SiAutocompleteListboxDirective, SiAutocompleteOptionDirective]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { SiAutocompleteDirective, SiAutocompleteListboxDirective, SiAutocompleteModule, SiAutocompleteOptionDirective };
